import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  deleteDoc
} from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "./config";

/**
 * Generates a unique token for NFC card authentication
 * @param {string} userId - The user ID to associate with this token
 * @returns {Promise<string>} The generated token
 */
export const generateNfcToken = async (userId) => {
  try {
    // Generate a unique token
    const token = Math.random().toString(36).substring(2, 15) + 
                 Math.random().toString(36).substring(2, 15);
    
    // Store the token in Firestore with the user ID
    await setDoc(doc(db, "nfcTokens", token), {
      userId: userId,
      createdAt: serverTimestamp(),
      // Token expires after 5 years (you can adjust this)
      expiresAt: new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000),
    });
    
    return token;
  } catch (error) {
    console.error("Error generating NFC token:", error);
    throw error;
  }
};

/**
 * Authenticates a user using an NFC token
 * @param {string} token - The token from the NFC card
 * @returns {Promise<object>} User data
 */
export const authenticateWithNfcToken = async (token) => {
  try {
    // Look up the token in Firestore
    const tokenDoc = await getDoc(doc(db, "nfcTokens", token));
    
    if (!tokenDoc.exists()) {
      throw new Error("Invalid token");
    }
    
    const tokenData = tokenDoc.data();
    
    // Check if token is expired
    if (tokenData.expiresAt && tokenData.expiresAt.toDate() < new Date()) {
      // Optionally clean up expired token
      await deleteDoc(doc(db, "nfcTokens", token));
      throw new Error("Token has expired");
    }
    
    const userId = tokenData.userId;
    
    // Get the user document
    const userDoc = await getDoc(doc(db, "users", userId));
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    
    // Sign in the user using their email and a special NFC password
    // This assumes you've stored an nfcAuthSecret in the user document
    // For simplicity, you could use a default password for all NFC logins
    await signInWithEmailAndPassword(
      auth, 
      userData.email, 
      userData.nfcAuthSecret || "nfc-default-password"
    );
    
    return {
      userId,
      email: userData.email,
    };
  } catch (error) {
    console.error("NFC token authentication error:", error);
    throw error;
  }
};

/**
 * Gets all tokens for a specific user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of token objects
 */
export const getUserNfcTokens = async (userId) => {
  try {
    const tokensQuery = query(
      collection(db, "nfcTokens"), 
      where("userId", "==", userId)
    );
    
    const snapshot = await getDocs(tokensQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore timestamps to JS Date objects
      createdAt: doc.data().createdAt?.toDate(),
      expiresAt: doc.data().expiresAt?.toDate(),
    }));
  } catch (error) {
    console.error("Error fetching user NFC tokens:", error);
    throw error;
  }
};

/**
 * Revokes (deletes) an NFC token
 * @param {string} token - The token to revoke
 * @returns {Promise<void>}
 */
export const revokeNfcToken = async (token) => {
  try {
    await deleteDoc(doc(db, "nfcTokens", token));
  } catch (error) {
    console.error("Error revoking NFC token:", error);
    throw error;
  }
};

// The verifyNFCToken function that called the HTTP endpoint has been removed.
// Ensure that your NfcAuthHandler.jsx (and the verifyNFCToken function it imports from 
// src/services/firebase/auth.js) is updated to call the Firebase *Callable Function*
// 'verifyNfcToken' for the main NFC authentication flow.